import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listThings,
  listPeople,
  getPerson,
  createThing,
  updateThing,
  deleteThing,
  deleteThings,
  createPerson,
  listPlaces,
  createPlace,
  createThingTransaction,
  generateThingCode,
  getThingInventoryState,
  checkThingDependencies,
  checkBulkDependencies,
  type Thing,
  type ThingCreate,
  type ThingUpdate,
  type ThingTrackingMode,
  type ThingInventoryState,
  type DependencyCheckResult,
} from '@littlebooker/dal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ViewToggle } from '@/components/ViewToggle';
import { SearchFilter } from '@/components/SearchFilter';
import { AsyncSearchableSelect, type SelectOption } from '@/components/AsyncSearchableSelect';
import { useViewStore } from '@/lib/viewStore';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { DangerZone } from '@/components/DangerZone';
import { PhotoUploadField } from '@/components/PhotoUploadField';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, ArrowUpRight, ArrowDownLeft, Flame, Copy, Check, RefreshCw, Package, User, MapPin, Clock } from 'lucide-react';

const THING_STATUSES: Array<Thing['status']> = ['available', 'in_use', 'maintenance', 'retired'];
const DEFAULT_METADATA_TEXT = '{\n  \n}';

// Derived inventory availability, computed from the (possibly not-yet-loaded)
// inventory state. Used both to filter the list and to count the filter pills.
// A missing state is treated as fully available (nothing checked out / consumed).
type AvailabilityBucket = 'full' | 'partial' | 'out';
const availabilityBucket = (inv: ThingInventoryState | undefined): AvailabilityBucket => {
  if (!inv) return 'full';
  if (inv.available <= 0) return 'out';
  if (inv.available < inv.total_quantity) return 'partial';
  return 'full';
};
type AvailabilityFilter = 'all' | 'attention' | 'partial' | 'out' | 'full';
const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

export default function ThingsPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createAnother, setCreateAnother] = useState(false);
  const [createFormKey, setCreateFormKey] = useState(0);
  const [createImageUrl, setCreateImageUrl] = useState('');
  const [createSlugPreview, setCreateSlugPreview] = useState('');
  const [createCode, setCreateCode] = useState('');
  const [createCodeCopied, setCreateCodeCopied] = useState(false);
  const [editingThing, setEditingThing] = useState<Thing | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [metadataInput, setMetadataInput] = useState(DEFAULT_METADATA_TEXT);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const viewMode = useViewStore((state) => state.getViewMode('things'));
  const setViewMode = useViewStore((state) => state.setViewMode);
  // Whether the list is grouped thing-first (default) or person-first ("who has what borrowed").
  const [groupBy, setGroupBy] = useState<'thing' | 'person'>('thing');
  // Person view: order people by name (default) or by most recent checkout.
  const [personSort, setPersonSort] = useState<'name' | 'recent'>('name');
  // Person view: only show items taken out today.
  const [borrowedTodayOnly, setBorrowedTodayOnly] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Delete functionality
  const { user, account } = useAuth();
  const canDelete = !user || user.role === 'owner' || user.role === 'admin';
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingThing, setDeletingThing] = useState<{ id: string; name: string } | null>(null);
  const [deleteDependencies, setDeleteDependencies] = useState<DependencyCheckResult | null>(null);
  const [selectedThingIds, setSelectedThingIds] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteDependencies, setBulkDeleteDependencies] = useState<Map<string, DependencyCheckResult>>(new Map());
  const [checkingSingleDependencies, setCheckingSingleDependencies] = useState<Set<string>>(new Set());
  const [checkingBulkDependencies, setCheckingBulkDependencies] = useState(false);
  // Inventory action state
  const [txPersonId, setTxPersonId] = useState<string | null>(null);
  const [txPersonInitialOption, setTxPersonInitialOption] = useState<SelectOption | null>(null);
  const [txQuantity, setTxQuantity] = useState<number | ''>(1);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  // Inline table action: which thing has the quick-action row open, and what mode
  const [inlineActionThingId, setInlineActionThingId] = useState<string | null>(null);
  const [inlineActionMode, setInlineActionMode] = useState<'checkout' | 'return' | 'consume'>('checkout');
  const [quickCreateName, setQuickCreateName] = useState('');
  const [quickCreatePhone, setQuickCreatePhone] = useState('');
  // Optional "where" (place) for a checkout — mirrors the person picker
  const [txPlaceId, setTxPlaceId] = useState<string | null>(null);
  const [txPlaceInitialOption, setTxPlaceInitialOption] = useState<SelectOption | null>(null);
  const [showPlaceQuickCreate, setShowPlaceQuickCreate] = useState(false);
  const [placeQuickCreateName, setPlaceQuickCreateName] = useState('');
  // Trigger buttons for the "where" pickers, so picking a person can move focus
  // straight to the place field (clicking a person unmounts the dropdown option,
  // which would otherwise drop focus to <body> and break tabbing).
  const inlinePlaceTriggerRef = useRef<HTMLButtonElement>(null);
  const dialogPlaceTriggerRef = useRef<HTMLButtonElement>(null);
  // Bumped to open a "where" picker so its search box is focused, ready to type.
  const [placeOpenSignal, setPlaceOpenSignal] = useState(0);
  // After picking a person, open the place picker so you can type a place
  // immediately (deferred so it runs after the person dropdown closes).
  const openPlacePicker = useCallback(() => {
    setTimeout(() => setPlaceOpenSignal((n) => n + 1), 0);
  }, []);
  // After a place is chosen/created, focus the trigger so Enter confirms checkout.
  const focusPlaceTrigger = useCallback(() => {
    setTimeout(() => {
      (inlinePlaceTriggerRef.current ?? dialogPlaceTriggerRef.current)?.focus();
    }, 0);
  }, []);
  // Owner picker state (both create + edit dialogs)
  const [createOwnerId, setCreateOwnerId] = useState<string | null>(null);
  const [editOwnerId, setEditOwnerId] = useState<string | null>(null);
  const [ownerInitialOption, setOwnerInitialOption] = useState<SelectOption | null>(null);
  const [ownerQuickCreateContext, setOwnerQuickCreateContext] = useState<null | 'create' | 'edit'>(null);
  const [ownerQuickCreateName, setOwnerQuickCreateName] = useState('');
  const [ownerQuickCreatePhone, setOwnerQuickCreatePhone] = useState('');

  // Fetch inventory state for the currently editing thing
  const { data: editingInventory } = useQuery({
    queryKey: ['thing-inventory', editingThing?.id],
    queryFn: () => getThingInventoryState(editingThing!.id),
    enabled: !!editingThing,
  });

  const transactionMutation = useMutation({
    mutationFn: createThingTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thing-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['thing-inventory-states'] });
      setTxPersonId(null);
      setTxQuantity(1);
      setTxPlaceId(null);
      setTxPlaceInitialOption(null);
      setShowPlaceQuickCreate(false);
      setPlaceQuickCreateName('');
    },
  });

  const quickCreatePersonMutation = useMutation({
    mutationFn: (data: { name: string; phone: string }) =>
      createPerson({
        name: data.name,
        slug: slugify(data.name),
        type: 'staff',
        phone: data.phone || null,
        email: `${slugify(data.name)}@placeholder.local`,
      } as Parameters<typeof createPerson>[0]),
    onSuccess: (newPerson) => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      setTxPersonInitialOption({
        id: newPerson.id,
        name: newPerson.name || 'Unnamed',
        subtitle: newPerson.phone || newPerson.type,
      });
      setTxPersonId(newPerson.id);
      setShowQuickCreate(false);
      setQuickCreateName('');
      setQuickCreatePhone('');
      openPlacePicker();
    },
  });

  const quickCreateOwnerMutation = useMutation({
    mutationFn: (data: { name: string; phone: string }) =>
      createPerson({
        name: data.name,
        slug: slugify(data.name),
        type: 'staff',
        phone: data.phone || null,
        email: `${slugify(data.name)}@placeholder.local`,
      } as Parameters<typeof createPerson>[0]),
    onSuccess: (newPerson) => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      const option: SelectOption = {
        id: newPerson.id,
        name: newPerson.name || 'Unnamed',
        subtitle: newPerson.phone || newPerson.type,
      };
      setOwnerInitialOption(option);
      if (ownerQuickCreateContext === 'edit') {
        setEditOwnerId(newPerson.id);
      } else {
        setCreateOwnerId(newPerson.id);
      }
      setOwnerQuickCreateContext(null);
      setOwnerQuickCreateName('');
      setOwnerQuickCreatePhone('');
    },
  });

  const searchPeople = useCallback(async (query: string): Promise<SelectOption[]> => {
    const people = await listPeople({ query } as Parameters<typeof listPeople>[0]);
    return people.map((p) => ({ id: p.id, name: p.name || 'Unnamed', subtitle: p.phone || p.type }));
  }, []);

  // Places ("where" a checkout goes). listPlaces has no server-side query, so
  // filter client-side to match the picker's typed query.
  const searchPlaces = useCallback(async (query: string): Promise<SelectOption[]> => {
    const places = await listPlaces({ is_active: true });
    const q = query.trim().toLowerCase();
    return places
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .map((p) => ({ id: p.id, name: p.name || 'Unnamed', subtitle: p.type }));
  }, []);

  const quickCreatePlaceMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      createPlace({
        name: data.name,
        slug: slugify(data.name),
        type: 'room',
      } as Parameters<typeof createPlace>[0]),
    onSuccess: (newPlace) => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      setTxPlaceInitialOption({
        id: newPlace.id,
        name: newPlace.name || 'Unnamed',
        subtitle: newPlace.type,
      });
      setTxPlaceId(newPlace.id);
      setShowPlaceQuickCreate(false);
      setPlaceQuickCreateName('');
      focusPlaceTrigger();
    },
  });

  // Seed owner picker when opening the edit dialog
  useEffect(() => {
    if (!editingThing) {
      setEditOwnerId(null);
      return;
    }
    setEditOwnerId(editingThing.owner_id ?? null);
    if (editingThing.owner_id) {
      getPerson(editingThing.owner_id).then((p) => {
        if (p) {
          setOwnerInitialOption({
            id: p.id,
            name: p.name || 'Unnamed',
            subtitle: p.phone || p.type,
          });
        }
      });
    } else {
      setOwnerInitialOption(null);
    }
  }, [editingThing]);

  const handleTransaction = (thingId: string, type: 'checkout' | 'return' | 'consume', personId?: string, qty?: number) => {
    const pid = personId || txPersonId;
    if (!pid && type !== 'consume') return;
    transactionMutation.mutate({
      thing_id: thingId,
      person_id: pid,
      // "Where" only applies when an item leaves (checkout/consume), not on return.
      place_id: type === 'return' ? null : txPlaceId,
      type,
      quantity: qty ?? (txQuantity === '' ? 1 : txQuantity),
    }, {
      onSuccess: () => {
        setInlineActionThingId(null);
      },
    });
  };

  const openInlineAction = (thingId: string, mode: 'checkout' | 'return' | 'consume', e: React.MouseEvent) => {
    e.stopPropagation();
    if (inlineActionThingId === thingId && inlineActionMode === mode) {
      setInlineActionThingId(null);
      return;
    }
    setInlineActionThingId(thingId);
    setInlineActionMode(mode);
    setTxPersonId(null);
    setTxQuantity(1);
    setShowQuickCreate(false);
    setQuickCreateName('');
    setQuickCreatePhone('');
    setTxPlaceId(null);
    setTxPlaceInitialOption(null);
    setShowPlaceQuickCreate(false);
    setPlaceQuickCreateName('');
  };

  const startEditingThing = (thing: Thing) => {
    setEditingThing(thing);
    setMetadataError(null);
    setImageUrl(thing.image_url || '');
    setTxPersonId(null);
    setTxQuantity(1);
    setShowQuickCreate(false);
    setTxPlaceId(null);
    setTxPlaceInitialOption(null);
    setShowPlaceQuickCreate(false);
    setPlaceQuickCreateName('');
    setMetadataInput(
      thing.metadata && Object.keys(thing.metadata).length > 0
        ? JSON.stringify(thing.metadata, null, 2)
        : DEFAULT_METADATA_TEXT
    );
  };

  useEffect(() => {
    if (!editingThing) {
      setMetadataInput(DEFAULT_METADATA_TEXT);
      setMetadataError(null);
      setImageUrl('');
    }
  }, [editingThing]);

  const { data: things = [], isLoading } = useQuery({
    queryKey: ['things'],
    queryFn: () => listThings(),
  });

  // Fetch inventory states for all things
  const { data: inventoryStates = {} } = useQuery({
    queryKey: ['thing-inventory-states', things.map((t) => t.id).join(',')],
    queryFn: async () => {
      const states = await Promise.all(things.map((t) => getThingInventoryState(t.id)));
      return Object.fromEntries(things.map((t, i) => [t.id, states[i]])) as Record<string, ThingInventoryState>;
    },
    enabled: things.length > 0,
  });

  // Handle deep-link from global search - highlight the entity
  useEffect(() => {
    const state = location.state as { openEntityId?: string; entityType?: string } | null;
    if (state?.openEntityId && state?.entityType === 'thing' && things.length > 0) {
      const thing = things.find((t) => t.id === state.openEntityId);
      if (thing) {
        // Open the edit dialog for this thing
        startEditingThing(thing);
        // Clear the state so it doesn't re-trigger
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [location.state, things, navigate, location.pathname]);

  // Auto-open a thing when the search box holds its exact code. The ref guards
  // against re-opening: it fires once per distinct exact code, so closing the
  // dialog (while the code is still in the box) won't immediately re-open it.
  const autoOpenedCodeRef = useRef<string | null>(null);
  useEffect(() => {
    const q = searchQuery.trim().toUpperCase();
    if (!q) {
      autoOpenedCodeRef.current = null; // cleared — let the same code re-open later
      return;
    }
    if (autoOpenedCodeRef.current === q) return;
    const match = things.find((t) => t.code?.toUpperCase() === q);
    if (match) {
      autoOpenedCodeRef.current = q;
      startEditingThing(match);
    } else {
      // Not a complete code (yet) — allow a later exact match to open.
      autoOpenedCodeRef.current = null;
    }
  }, [searchQuery, things]);

  // Copy the editing thing's code to the clipboard (with a non-secure-context
  // fallback), flashing a "Copied" state for 2s.
  const copyEditingCode = useCallback(async () => {
    const code = editingThing?.code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      window.prompt('Copy code', code);
    }
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  }, [editingThing]);

  // Mint a fresh lookup code each time the create form opens (and on each
  // "create another" reset, which bumps createFormKey) so the user can see —
  // and copy/label — the code before saving. createThing tries this code first
  // and only regenerates on the rare collision, so it's what-you-see in
  // practice. Regenerating is just a convenience for avoiding look-alikes.
  useEffect(() => {
    if (showCreateForm) setCreateCode(generateThingCode());
  }, [showCreateForm, createFormKey]);

  const copyCreateCode = useCallback(async () => {
    if (!createCode) return;
    try {
      await navigator.clipboard.writeText(createCode);
    } catch {
      window.prompt('Copy code', createCode);
    }
    setCreateCodeCopied(true);
    setTimeout(() => setCreateCodeCopied(false), 2000);
  }, [createCode]);

  // Filter things based on search query and availability bucket
  const filteredThings = useMemo(() => {
    const getCategory = (thing: Thing) => ((thing as unknown as { category?: string }).category ?? thing.type ?? '').toLowerCase();
    const lowerQuery = searchQuery.trim().toLowerCase();
    return things.filter((thing) => {
      if (lowerQuery) {
        const matchesSearch =
          thing.name.toLowerCase().includes(lowerQuery) ||
          (thing.code ?? '').toLowerCase().includes(lowerQuery) ||
          thing.slug.toLowerCase().includes(lowerQuery) ||
          getCategory(thing).includes(lowerQuery) ||
          thing.status.toLowerCase().includes(lowerQuery);
        if (!matchesSearch) return false;
      }
      if (availabilityFilter !== 'all') {
        const bucket = availabilityBucket(inventoryStates[thing.id]);
        if (availabilityFilter === 'attention') return bucket !== 'full';
        return bucket === availabilityFilter;
      }
      return true;
    });
  }, [things, searchQuery, availabilityFilter, inventoryStates]);

  // Counts per availability bucket, for the filter pill badges
  const availabilityCounts = useMemo(() => {
    const counts = { full: 0, partial: 0, out: 0 };
    for (const thing of things) {
      counts[availabilityBucket(inventoryStates[thing.id])] += 1;
    }
    return counts;
  }, [things, inventoryStates]);

  // Person-first inversion of the inventory map: who currently has what borrowed.
  // Only lendable things contribute active checkouts (consumables return none).
  const borrowedByPerson = useMemo(() => {
    const map = new Map<
      string,
      { person_id: string; person_name: string; items: Array<{ thing: Thing; quantity: number; place_name?: string; checked_out_at?: string }> }
    >();
    for (const thing of things) {
      const inv = inventoryStates[thing.id];
      if (!inv) continue;
      for (const co of inv.active_checkouts) {
        const entry =
          map.get(co.person_id) ?? { person_id: co.person_id, person_name: co.person_name, items: [] };
        entry.items.push({ thing, quantity: co.quantity, place_name: co.place_name, checked_out_at: co.checked_out_at });
        map.set(co.person_id, entry);
      }
    }
    let list = Array.from(map.values());

    // "Borrowed today" filter: keep only items checked out since local midnight,
    // and drop people left with nothing.
    if (borrowedTodayOnly) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const startMs = startOfToday.getTime();
      list = list
        .map((p) => ({
          ...p,
          items: p.items.filter(
            (i) => i.checked_out_at && new Date(i.checked_out_at).getTime() >= startMs
          ),
        }))
        .filter((p) => p.items.length > 0);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.person_name.toLowerCase().includes(q) ||
          p.items.some(
            (i) =>
              i.thing.name.toLowerCase().includes(q) ||
              (i.thing.code ?? '').toLowerCase().includes(q)
          )
      );
    }

    // Most recent checkout timestamp a person has, for sorting / "recent" order.
    const latestMs = (p: { items: Array<{ checked_out_at?: string }> }) =>
      p.items.reduce((max, i) => {
        const t = i.checked_out_at ? new Date(i.checked_out_at).getTime() : 0;
        return t > max ? t : max;
      }, 0);

    if (personSort === 'recent') {
      // Within each person, newest items first too.
      list.forEach((p) =>
        p.items.sort(
          (a, b) =>
            (b.checked_out_at ? new Date(b.checked_out_at).getTime() : 0) -
            (a.checked_out_at ? new Date(a.checked_out_at).getTime() : 0)
        )
      );
      list.sort((a, b) => latestMs(b) - latestMs(a));
    } else {
      list.sort((a, b) => a.person_name.localeCompare(b.person_name));
    }
    return list;
  }, [things, inventoryStates, searchQuery, personSort, borrowedTodayOnly]);

  // Press "/" anywhere on the page to jump to the search box (GitHub/Linear style).
  // Ignored while typing in a field or while a dialog is open.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      if (showCreateForm || editingThing || deleteDialogOpen || bulkDeleteDialogOpen) return;
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showCreateForm, editingThing, deleteDialogOpen, bulkDeleteDialogOpen]);

  const createMutation = useMutation({
    mutationFn: (data: ThingCreate) => createThing(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['things'] });
      if (createAnother) {
        setCreateFormKey((k) => k + 1);
        setCreateImageUrl('');
        setCreateSlugPreview('');
        setCreateOwnerId(null);
        setOwnerInitialOption(null);
      } else {
        setShowCreateForm(false);
        setCreateOwnerId(null);
        setOwnerInitialOption(null);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ThingUpdate }) => updateThing(id, patch),
    onSuccess: (updatedThing) => {
      queryClient.setQueryData(['things'], (existing: Thing[] | undefined) => {
        if (!existing) return existing;
        return existing.map((t) => (t.id === updatedThing.id ? updatedThing : t));
      });
      queryClient.invalidateQueries({ queryKey: ['things'] });
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setEditingThing(null);
    },
  });

  // Delete mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteThing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['things'] });
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setDeleteDialogOpen(false);
      setDeletingThing(null);
      setDeleteDependencies(null);
      setEditingThing(null);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => deleteThings(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['things'] });
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setBulkDeleteDialogOpen(false);
      setSelectedThingIds(new Set());
      setBulkDeleteDependencies(new Map());
    },
  });

  // Delete handlers
  const handleDeleteClick = async (thing: { id: string; name: string }, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!canDelete) return;
    
    setCheckingSingleDependencies((prev) => new Set(prev).add(thing.id));
    try {
      setDeletingThing(thing);
      const dependencies = await checkThingDependencies(thing.id);
      setDeleteDependencies(dependencies);
      setDeleteDialogOpen(true);
    } finally {
      setCheckingSingleDependencies((prev) => {
        const next = new Set(prev);
        next.delete(thing.id);
        return next;
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (deletingThing) {
      deleteMutation.mutate(deletingThing.id);
    }
  };

  const handleBulkDeleteClick = async () => {
    if (selectedThingIds.size === 0 || !canDelete) return;
    
    setCheckingBulkDependencies(true);
    try {
      const ids = Array.from(selectedThingIds);
      const dependencies = await checkBulkDependencies('thing', ids);
      setBulkDeleteDependencies(dependencies);
      setBulkDeleteDialogOpen(true);
    } finally {
      setCheckingBulkDependencies(false);
    }
  };

  const handleBulkDeleteConfirm = () => {
    if (selectedThingIds.size > 0) {
      bulkDeleteMutation.mutate(Array.from(selectedThingIds));
    }
  };

  const toggleThingSelection = (thingId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedThingIds((prev) => {
      const next = new Set(prev);
      if (next.has(thingId)) {
        next.delete(thingId);
      } else {
        next.add(thingId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedThingIds.size === filteredThings.length) {
      setSelectedThingIds(new Set());
    } else {
      setSelectedThingIds(new Set(filteredThings.map((t) => t.id)));
    }
  };

  const handleThingEditSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingThing) return;

    const formData = new FormData(event.currentTarget);
    const name = (formData.get('name') as string).trim();
    const slugInput = (formData.get('slug') as string).trim();
    const type = (formData.get('type') as string).trim();
    const status = (formData.get('status') as string).trim() as Thing['status'];
    const quantityRaw = (formData.get('quantity') as string) ?? '1';
    const quantity = parseInt(quantityRaw, 10) || 1;
    const metadataRaw = (formData.get('metadata') as string) ?? '';

    const slug = slugInput.length > 0 ? slugInput : slugify(name);
    let metadata: Record<string, unknown> | undefined;

    if (metadataRaw.trim().length > 0) {
      try {
        metadata = JSON.parse(metadataRaw);
        setMetadataError(null);
      } catch (error) {
        setMetadataError('Metadata must be valid JSON.');
        return;
      }
    } else {
      metadata = {};
      setMetadataError(null);
    }

    const trackingMode = (formData.get('tracking_mode') as string) as ThingTrackingMode || editingThing.tracking_mode;

    updateMutation.mutate({
      id: editingThing.id,
      patch: {
        name,
        slug,
        type: type || undefined,
        status,
        tracking_mode: trackingMode,
        quantity,
        image_url: imageUrl || null,
        owner_id: editOwnerId,
        metadata,
      },
    });
  };

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const name = (formData.get('name') as string).trim();
    const slugInput = (formData.get('slug') as string).trim();
    const type = (formData.get('type') as string).trim();
    const status = (formData.get('status') as string).trim() as Thing['status'];
    const quantityRaw = (formData.get('quantity') as string) ?? '1';
    const quantity = parseInt(quantityRaw, 10) || 1;
    const metadataRaw = (formData.get('metadata') as string) ?? '';

    const slug = slugInput.length > 0 ? slugInput : slugify(name);
    let metadata: Record<string, unknown> = {};

    if (metadataRaw.trim().length > 0) {
      try {
        metadata = JSON.parse(metadataRaw);
      } catch (error) {
        return;
      }
    }

    const trackingMode = (formData.get('tracking_mode') as string) as ThingTrackingMode || 'lendable';

    createMutation.mutate({
      name,
      slug,
      type: type || 'equipment',
      status: status || 'available',
      tracking_mode: trackingMode,
      quantity,
      code: createCode || undefined,
      image_url: createImageUrl || null,
      owner_id: createOwnerId,
      metadata,
    });
  };

  const getInventoryLabel = (thing: Thing) => {
    const inv = inventoryStates[thing.id];
    if (!inv) return `${thing.quantity ?? 1}`;
    if (thing.tracking_mode === 'consumable') {
      return `${inv.available} / ${inv.total_quantity} remaining`;
    }
    return `${inv.available} / ${inv.total_quantity} available`;
  };

  const getInventoryColor = (thing: Thing) => {
    const inv = inventoryStates[thing.id];
    if (!inv) return 'text-muted-foreground';
    if (inv.available === inv.total_quantity) return 'text-green-600';
    if (inv.available > 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-badge text-green-badge-foreground';
      case 'in_use':
        return 'bg-blue-badge text-blue-badge-foreground';
      case 'maintenance':
        return 'bg-yellow-badge text-yellow-badge-foreground';
      case 'retired':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Things</h1>
          <p className="text-muted-foreground">Manage equipment, instruments, and other items</p>
        </div>
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Thing
        </Button>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <SearchFilter
            ref={searchInputRef}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search things by name, category, or status...  ( / )"
            className="w-full max-w-sm"
          />
          <ViewToggle
            view={viewMode}
            onViewChange={(mode) => setViewMode('things', mode)}
            availableViews={['list']}
          />
          <div className="inline-flex items-center rounded-md border border-input bg-background p-1">
            <button
              type="button"
              onClick={() => setGroupBy('thing')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-sm font-medium transition-colors',
                groupBy === 'thing'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              aria-pressed={groupBy === 'thing'}
              title="Group by thing"
            >
              <Package className="h-4 w-4" />
              By thing
            </button>
            <button
              type="button"
              onClick={() => setGroupBy('person')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-sm font-medium transition-colors',
                groupBy === 'person'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              aria-pressed={groupBy === 'person'}
              title="Group by person — who has what borrowed"
            >
              <User className="h-4 w-4" />
              By person
            </button>
          </div>
          {groupBy === 'thing' && (searchQuery || availabilityFilter !== 'all') && (
            <span className="text-sm text-muted-foreground">
              {filteredThings.length} of {things.length} things
            </span>
          )}
        </div>

        <div className={cn('flex flex-wrap items-center gap-2', groupBy === 'person' && 'hidden')}>
          {([
            { key: 'all', label: 'All', count: things.length, dot: null },
            { key: 'attention', label: 'Needs attention', count: availabilityCounts.partial + availabilityCounts.out, dot: null },
            { key: 'partial', label: 'Partial', count: availabilityCounts.partial, dot: 'bg-yellow-500' },
            { key: 'out', label: 'Out of stock', count: availabilityCounts.out, dot: 'bg-red-500' },
            { key: 'full', label: 'Fully available', count: availabilityCounts.full, dot: 'bg-green-500' },
          ] as Array<{ key: AvailabilityFilter; label: string; count: number; dot: string | null }>).map((f) => (
            <Button
              key={f.key}
              type="button"
              size="sm"
              variant={availabilityFilter === f.key ? 'default' : 'outline'}
              onClick={() => setAvailabilityFilter(f.key)}
            >
              {f.dot && <span className={`mr-2 h-2 w-2 rounded-full ${f.dot}`} />}
              {f.label}
              <span className="ml-2 text-xs opacity-70">{f.count}</span>
            </Button>
          ))}
        </div>
        {groupBy === 'person' && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={borrowedTodayOnly ? 'default' : 'outline'}
              onClick={() => setBorrowedTodayOnly((v) => !v)}
              title="Only show items taken out today"
            >
              <Clock className="mr-1.5 h-4 w-4" />
              Today
            </Button>
            <span className="ml-1 text-sm text-muted-foreground">Sort:</span>
            <div className="inline-flex items-center rounded-md border border-input bg-background p-1">
              <button
                type="button"
                onClick={() => setPersonSort('name')}
                className={cn(
                  'rounded-sm px-2.5 py-1 text-sm font-medium transition-colors',
                  personSort === 'name'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
                aria-pressed={personSort === 'name'}
              >
                Name
              </button>
              <button
                type="button"
                onClick={() => setPersonSort('recent')}
                className={cn(
                  'rounded-sm px-2.5 py-1 text-sm font-medium transition-colors',
                  personSort === 'recent'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
                aria-pressed={personSort === 'recent'}
                title="Most recent checkout first"
              >
                Recent
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions bar */}
      {selectedThingIds.size > 0 && canDelete && (
        <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
          <span className="text-sm font-medium">
            {selectedThingIds.size} thing{selectedThingIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedThingIds(new Set())}
              disabled={checkingBulkDependencies}
            >
              Clear selection
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDeleteClick}
              disabled={checkingBulkDependencies}
            >
              {checkingBulkDependencies ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Checking dependencies...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete {selectedThingIds.size}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent size="medium" className="flex max-h-[90vh] flex-col">
          <DialogHeader>
            <DialogTitle>Create New Thing</DialogTitle>
            <DialogDescription>Add a new equipment, instrument, or item</DialogDescription>
          </DialogHeader>
          <form key={createFormKey} onSubmit={handleCreate} className="flex min-h-0 flex-col gap-4">
            <div className="space-y-4 overflow-y-auto px-4 sm:px-6">
              {/* Lookup code — assigned up front so it can be labelled before saving */}
              <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Lookup code</p>
                  <button
                    type="button"
                    onClick={copyCreateCode}
                    title="Click to copy"
                    className="font-mono text-lg tracking-widest hover:text-primary"
                  >
                    {createCode}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCreateCode(generateThingCode())} title="Generate a different code">
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={copyCreateCode}>
                    {createCodeCopied ? (
                      <><Check className="mr-1 h-3 w-3" /> Copied</>
                    ) : (
                      <><Copy className="mr-1 h-3 w-3" /> Copy</>
                    )}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <input
                    name="name"
                    required
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    placeholder="Microphone"
                    onChange={(e) => setCreateSlugPreview(slugify(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Slug</label>
                  <input
                    name="slug"
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    placeholder="microphone"
                  />
                  <p className="text-xs text-muted-foreground">Auto-generated if empty</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <input
                    name="type"
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    placeholder="equipment, instrument, furniture..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <select
                    name="status"
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    defaultValue="available"
                  >
                    {THING_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tracking Mode</label>
                  <select
                    name="tracking_mode"
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    defaultValue="lendable"
                  >
                    <option value="lendable">Lendable (check in/out)</option>
                    <option value="consumable">Consumable (used up)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Quantity</label>
                  <input
                    name="quantity"
                    type="number"
                    min="1"
                    defaultValue="1"
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Total stock</p>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Owner</label>
                {ownerQuickCreateContext === 'create' ? (
                  <div className="space-y-2 rounded-md border bg-background p-3">
                    <p className="text-xs font-medium">Quick-add person</p>
                    <input
                      type="text"
                      placeholder="Name"
                      value={ownerQuickCreateName}
                      onChange={(e) => setOwnerQuickCreateName(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    />
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={ownerQuickCreatePhone}
                      onChange={(e) => setOwnerQuickCreatePhone(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={!ownerQuickCreateName.trim() || quickCreateOwnerMutation.isPending}
                        onClick={() => quickCreateOwnerMutation.mutate({ name: ownerQuickCreateName.trim(), phone: ownerQuickCreatePhone.trim() })}
                      >
                        {quickCreateOwnerMutation.isPending ? 'Creating...' : 'Add'}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setOwnerQuickCreateContext(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <AsyncSearchableSelect
                        searchFn={searchPeople}
                        selectedIds={createOwnerId ? [createOwnerId] : []}
                        initialOptions={ownerInitialOption && createOwnerId === ownerInitialOption.id ? [ownerInitialOption] : undefined}
                        onSelect={(opt) => {
                          setCreateOwnerId(opt.id);
                          setOwnerInitialOption(opt);
                        }}
                        placeholder="No owner"
                        searchPlaceholder="Search people..."
                        emptyMessage="No one found"
                        onCreateNew={(query) => {
                          setOwnerQuickCreateName(query);
                          setOwnerQuickCreatePhone('');
                          setOwnerQuickCreateContext('create');
                        }}
                        createNewLabel="Create person"
                      />
                    </div>
                    {createOwnerId && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setCreateOwnerId(null)}>
                        Clear
                      </Button>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Optional. Person responsible for this item.</p>
              </div>
              <PhotoUploadField
                label="Image"
                helper="Upload a photo of this item"
                placeholder="https://"
                value={createImageUrl}
                onChange={setCreateImageUrl}
                category="thing_photo"
                identifier={createSlugPreview || 'new-thing'}
                accountSlug={account?.slug}
                allowBypassSizeLimit
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">Metadata (JSON)</label>
                <textarea
                  name="metadata"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                  rows={4}
                  placeholder={DEFAULT_METADATA_TEXT}
                />
                <p className="text-xs text-muted-foreground">Optional JSON metadata</p>
              </div>
            </div>
            <DialogFooter className="gap-2 border-t pt-4 sm:justify-between">
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={createAnother}
                  onChange={(e) => setCreateAnother(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                Create another
              </label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <p>Loading...</p>
      ) : groupBy === 'person' ? (
        <div className="space-y-4">
          {borrowedByPerson.length === 0 ? (
            <div className="rounded-md border p-8 text-center text-muted-foreground">
              {searchQuery
                ? 'No borrowed items match your search.'
                : borrowedTodayOnly
                  ? 'Nothing was taken out today.'
                  : 'Nothing is currently borrowed.'}
            </div>
          ) : (
            borrowedByPerson.map((person) => {
              const totalItems = person.items.reduce((sum, i) => sum + i.quantity, 0);
              return (
                <Card key={person.person_id}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between text-base">
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {person.person_name}
                      </span>
                      <span className="text-sm font-normal text-muted-foreground">
                        {totalItems} item{totalItems !== 1 ? 's' : ''}
                        {person.items.length !== totalItems && (
                          <> across {person.items.length} thing{person.items.length !== 1 ? 's' : ''}</>
                        )}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="divide-y">
                      {person.items.map((item) => (
                        <div key={item.thing.id} className="flex items-center justify-between py-2">
                          <button
                            type="button"
                            className="flex items-center gap-2 text-left hover:underline"
                            onClick={() => startEditingThing(item.thing)}
                          >
                            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs tracking-wider text-muted-foreground">
                              {item.thing.code}
                            </span>
                            <span className="font-medium">{item.thing.name}</span>
                            {item.quantity > 1 && (
                              <span className="text-sm text-muted-foreground">×{item.quantity}</span>
                            )}
                            {item.place_name && (
                              <span className="inline-flex items-center gap-0.5 text-sm text-muted-foreground">
                                <MapPin className="h-3 w-3" />
                                {item.place_name}
                              </span>
                            )}
                            {item.checked_out_at && (
                              <span
                                className="inline-flex items-center gap-0.5 text-sm text-muted-foreground"
                                title={`Taken out ${new Date(item.checked_out_at).toLocaleString()}`}
                              >
                                <Clock className="h-3 w-3" />
                                {new Date(item.checked_out_at).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </span>
                            )}
                          </button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={transactionMutation.isPending}
                            onClick={() =>
                              transactionMutation.mutate({
                                thing_id: item.thing.id,
                                person_id: person.person_id,
                                type: 'return',
                                quantity: item.quantity,
                              })
                            }
                          >
                            <ArrowDownLeft className="mr-1 h-4 w-4" />
                            Return
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredThings.map((thing) => {
            const category = (thing as unknown as { category?: string }).category ?? thing.type ?? '—';
            return (
              <Card
                key={thing.id}
                className="relative cursor-pointer"
                onClick={() => startEditingThing(thing)}
              >
                {canDelete && (
                  <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedThingIds.has(thing.id)}
                      onChange={() => toggleThingSelection(thing.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 rounded border-gray-300"
                      disabled={checkingSingleDependencies.has(thing.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteClick(thing, e)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      disabled={checkingSingleDependencies.has(thing.id)}
                      title={checkingSingleDependencies.has(thing.id) ? 'Checking dependencies...' : 'Delete'}
                    >
                      {checkingSingleDependencies.has(thing.id) ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs tracking-wider text-muted-foreground">{thing.code}</span>
                        {thing.name}
                      </CardTitle>
                      <CardDescription>{category} • {thing.slug} • <span className={getInventoryColor(thing)}>{getInventoryLabel(thing)}</span></CardDescription>
                    </div>
                    <span className={`rounded px-2 py-1 text-xs font-medium ${getStatusColor(thing.status)}`}>
                      {thing.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {thing.metadata && Object.keys(thing.metadata).length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {JSON.stringify(thing.metadata, null, 2)}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {canDelete && (
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedThingIds.size === filteredThings.length && filteredThings.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </TableHead>
                )}
                <TableHead className="w-16">Photo</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Inventory</TableHead>
                <TableHead className="text-right">Quick Actions</TableHead>
                {canDelete && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredThings.map((thing) => {
                const category = (thing as unknown as { category?: string }).category ?? thing.type ?? '—';
                const inv = inventoryStates[thing.id];
                const isOpen = inlineActionThingId === thing.id;
                const colCount = canDelete ? 8 : 7;
                return (
                  <React.Fragment key={thing.id}>
                    <TableRow className="cursor-pointer" onClick={() => startEditingThing(thing)}>
                      {canDelete && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedThingIds.has(thing.id)}
                            onChange={() => toggleThingSelection(thing.id)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        {thing.image_url ? (
                          <img
                            src={thing.image_url}
                            alt={thing.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
                            —
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <span className="mr-2 rounded bg-muted px-1.5 py-0.5 font-mono text-xs tracking-wider text-muted-foreground">{thing.code}</span>
                        {thing.name}
                      </TableCell>
                      <TableCell>
                        <span className="rounded bg-secondary text-secondary-foreground px-2 py-0.5 text-xs font-medium capitalize">
                          {category}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${getStatusColor(thing.status)}`}>
                          {thing.status.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell className={`font-medium ${getInventoryColor(thing)}`}>{getInventoryLabel(thing)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {thing.tracking_mode === 'consumable' ? (
                            <Button
                              variant={isOpen && inlineActionMode === 'consume' ? 'default' : 'outline'}
                              size="sm"
                              className="h-7 text-xs"
                              disabled={!inv || inv.available < 1}
                              onClick={(e) => openInlineAction(thing.id, 'consume', e)}
                            >
                              <Flame className="mr-1 h-3 w-3" />
                              Use
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant={isOpen && inlineActionMode === 'checkout' ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 text-xs"
                                disabled={!inv || inv.available < 1}
                                onClick={(e) => openInlineAction(thing.id, 'checkout', e)}
                              >
                                <ArrowUpRight className="mr-1 h-3 w-3" />
                                Out
                              </Button>
                              {inv && inv.checked_out > 0 && (
                                <Button
                                  variant={isOpen && inlineActionMode === 'return' ? 'default' : 'outline'}
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={(e) => openInlineAction(thing.id, 'return', e)}
                                >
                                  <ArrowDownLeft className="mr-1 h-3 w-3" />
                                  In
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                      {canDelete && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteClick(thing, e)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            disabled={checkingSingleDependencies.has(thing.id)}
                            title={checkingSingleDependencies.has(thing.id) ? 'Checking dependencies...' : 'Delete'}
                          >
                            {checkingSingleDependencies.has(thing.id) ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>

                    {/* Inline action row */}
                    {isOpen && (
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableCell colSpan={colCount} className="py-3">
                          {inlineActionMode === 'return' && inv ? (
                            /* Return mode: show who has items, one-click return */
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-medium text-muted-foreground mr-1">Return from:</span>
                              {inv.active_checkouts.map((co: ThingInventoryState['active_checkouts'][number]) => (
                                <Button
                                  key={co.person_id}
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  disabled={transactionMutation.isPending}
                                  onClick={() => handleTransaction(thing.id, 'return', co.person_id, co.quantity)}
                                >
                                  <ArrowDownLeft className="mr-1 h-3 w-3" />
                                  {co.person_name} ({co.quantity})
                                  {co.place_name && (
                                    <span className="ml-1 inline-flex items-center gap-0.5 text-muted-foreground">
                                      <MapPin className="h-3 w-3" />
                                      {co.place_name}
                                    </span>
                                  )}
                                </Button>
                              ))}
                              <Button variant="ghost" size="sm" className="h-7 text-xs ml-auto" onClick={() => setInlineActionThingId(null)}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            /* Checkout / Consume mode: person picker + quantity + go */
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-medium text-muted-foreground mr-1">
                                {inlineActionMode === 'consume' ? 'Used by:' : 'Checkout to:'}
                              </span>
                              {!showQuickCreate ? (
                                <div className="w-56">
                                  <AsyncSearchableSelect
                                    searchFn={searchPeople}
                                    selectedIds={txPersonId ? [txPersonId] : []}
                                    initialOptions={txPersonInitialOption && txPersonId === txPersonInitialOption.id ? [txPersonInitialOption] : undefined}
                                    onSelect={(opt) => { setTxPersonId(opt.id); openPlacePicker(); }}
                                    placeholder="Pick person..."
                                    searchPlaceholder="Search..."
                                    emptyMessage="No one found"
                                    onCreateNew={(query) => {
                                      setQuickCreateName(query);
                                      setShowQuickCreate(true);
                                    }}
                                    createNewLabel="Create person"
                                  />
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    placeholder="Name"
                                    value={quickCreateName}
                                    onChange={(e) => setQuickCreateName(e.target.value)}
                                    className="h-8 w-32 rounded-md border border-input bg-background px-2 text-sm"
                                    autoFocus
                                  />
                                  <input
                                    type="tel"
                                    placeholder="Phone (optional)"
                                    value={quickCreatePhone}
                                    onChange={(e) => setQuickCreatePhone(e.target.value)}
                                    className="h-8 w-32 rounded-md border border-input bg-background px-2 text-sm"
                                  />
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs"
                                    disabled={!quickCreateName.trim() || quickCreatePersonMutation.isPending}
                                    onClick={() => quickCreatePersonMutation.mutate({ name: quickCreateName.trim(), phone: quickCreatePhone.trim() })}
                                  >
                                    {quickCreatePersonMutation.isPending ? '...' : 'Add'}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowQuickCreate(false)}>
                                    Back
                                  </Button>
                                </div>
                              )}
                              {!showPlaceQuickCreate ? (
                                <div className="w-44">
                                  <AsyncSearchableSelect
                                    triggerRef={inlinePlaceTriggerRef}
                                    openSignal={placeOpenSignal}
                                    onTriggerKeyDown={(e) => {
                                      if (e.key !== 'Enter') return;
                                      e.preventDefault();
                                      if ((!txPersonId && inlineActionMode !== 'consume') || transactionMutation.isPending) return;
                                      handleTransaction(thing.id, inlineActionMode);
                                    }}
                                    searchFn={searchPlaces}
                                    selectedIds={txPlaceId ? [txPlaceId] : []}
                                    initialOptions={txPlaceInitialOption && txPlaceId === txPlaceInitialOption.id ? [txPlaceInitialOption] : undefined}
                                    onSelect={(opt) => { setTxPlaceId(opt.id); setTimeout(() => inlinePlaceTriggerRef.current?.focus(), 0); }}
                                    placeholder="Where? (optional)"
                                    searchPlaceholder="Search places..."
                                    emptyMessage="No place found"
                                    onCreateNew={(query) => {
                                      setPlaceQuickCreateName(query);
                                      setShowPlaceQuickCreate(true);
                                    }}
                                    createNewLabel="Create place"
                                  />
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    placeholder="Place name"
                                    value={placeQuickCreateName}
                                    onChange={(e) => setPlaceQuickCreateName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key !== 'Enter') return;
                                      e.preventDefault();
                                      if (!placeQuickCreateName.trim() || quickCreatePlaceMutation.isPending) return;
                                      quickCreatePlaceMutation.mutate({ name: placeQuickCreateName.trim() });
                                    }}
                                    className="h-8 w-32 rounded-md border border-input bg-background px-2 text-sm"
                                    autoFocus
                                  />
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs"
                                    disabled={!placeQuickCreateName.trim() || quickCreatePlaceMutation.isPending}
                                    onClick={() => quickCreatePlaceMutation.mutate({ name: placeQuickCreateName.trim() })}
                                  >
                                    {quickCreatePlaceMutation.isPending ? '...' : 'Add'}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowPlaceQuickCreate(false)}>
                                    Back
                                  </Button>
                                </div>
                              )}
                              <input
                                type="number"
                                min="1"
                                max={inv?.available ?? 1}
                                value={txQuantity}
                                onChange={(e) => setTxQuantity(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                                onBlur={() => setTxQuantity((q) => (q === '' || q < 1) ? 1 : q)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter') return;
                                  e.preventDefault();
                                  if ((!txPersonId && inlineActionMode !== 'consume') || transactionMutation.isPending) return;
                                  handleTransaction(thing.id, inlineActionMode);
                                }}
                                className="h-8 w-16 rounded-md border border-input bg-background px-2 text-center text-sm"
                              />
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                disabled={(!txPersonId && inlineActionMode !== 'consume') || transactionMutation.isPending}
                                onClick={() => handleTransaction(thing.id, inlineActionMode)}
                              >
                                {transactionMutation.isPending ? '...' : inlineActionMode === 'consume' ? 'Use' : 'Checkout'}
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setInlineActionThingId(null)}>
                                Cancel
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Thing Dialog */}
      <Dialog open={!!editingThing} onOpenChange={(open) => !open && setEditingThing(null)}>
        <DialogContent size="medium" className="flex max-h-[90vh] flex-col">
          <DialogHeader>
            <DialogTitle>Edit Thing</DialogTitle>
            <DialogDescription>Update name, identifiers, status, and metadata.</DialogDescription>
          </DialogHeader>
          {editingThing && (
            <form key={editingThing.id} onSubmit={handleThingEditSubmit} className="flex min-h-0 flex-col gap-4">
              <div className="space-y-4 overflow-y-auto px-4 sm:px-6">

              {/* Lookup code — read-only, click to copy */}
              <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Lookup code</p>
                  <button
                    type="button"
                    onClick={copyEditingCode}
                    title="Click to copy"
                    className="font-mono text-lg tracking-widest hover:text-primary"
                  >
                    {editingThing.code}
                  </button>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={copyEditingCode}>
                  {codeCopied ? (
                    <><Check className="mr-1 h-3 w-3" /> Copied</>
                  ) : (
                    <><Copy className="mr-1 h-3 w-3" /> Copy</>
                  )}
                </Button>
              </div>

              {/* Inventory Section — at the top for fast access */}
              {editingInventory && (
                <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Inventory</h3>
                    <span className={`text-sm font-medium ${getInventoryColor(editingThing)}`}>
                      {getInventoryLabel(editingThing)}
                    </span>
                  </div>

                  {/* Active Checkouts (lendable only) */}
                  {editingThing.tracking_mode !== 'consumable' && editingInventory.active_checkouts.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Checked Out</p>
                      {editingInventory.active_checkouts.map((co: ThingInventoryState['active_checkouts'][number]) => (
                        <div key={co.person_id} className="flex items-center justify-between rounded-md border bg-background px-3 py-2 text-sm">
                          <span className="inline-flex items-center gap-1.5">
                            {co.person_name} &times; {co.quantity}
                            {co.place_name && (
                              <span className="inline-flex items-center gap-0.5 text-muted-foreground">
                                <MapPin className="h-3 w-3" />
                                {co.place_name}
                              </span>
                            )}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={transactionMutation.isPending}
                            onClick={() => handleTransaction(editingThing.id, 'return', co.person_id, co.quantity)}
                          >
                            <ArrowDownLeft className="mr-1 h-3 w-3" />
                            Return
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Transaction Action */}
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {editingThing.tracking_mode === 'consumable' ? 'Record Consumption' : 'Checkout'}
                    </p>

                    {!showQuickCreate ? (
                      <div className="space-y-2">
                        <AsyncSearchableSelect
                          searchFn={searchPeople}
                          selectedIds={txPersonId ? [txPersonId] : []}
                          initialOptions={txPersonInitialOption && txPersonId === txPersonInitialOption.id ? [txPersonInitialOption] : undefined}
                          onSelect={(opt) => { setTxPersonId(opt.id); openPlacePicker(); }}
                          placeholder="Select person..."
                          searchPlaceholder="Search by name..."
                          emptyMessage="No one found"
                          onCreateNew={(query) => {
                            setQuickCreateName(query);
                            setShowQuickCreate(true);
                          }}
                          createNewLabel="Create person"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 rounded-md border bg-background p-3">
                        <p className="text-xs font-medium">Quick-add person</p>
                        <input
                          type="text"
                          placeholder="Name"
                          value={quickCreateName}
                          onChange={(e) => setQuickCreateName(e.target.value)}
                          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                        />
                        <input
                          type="tel"
                          placeholder="Phone number (optional)"
                          value={quickCreatePhone}
                          onChange={(e) => setQuickCreatePhone(e.target.value)}
                          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            disabled={!quickCreateName.trim() || quickCreatePersonMutation.isPending}
                            onClick={() => quickCreatePersonMutation.mutate({ name: quickCreateName.trim(), phone: quickCreatePhone.trim() })}
                          >
                            {quickCreatePersonMutation.isPending ? 'Creating...' : 'Add'}
                          </Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => setShowQuickCreate(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Where (optional) — pick or quick-create a place */}
                    {editingThing.tracking_mode !== 'consumable' && (
                      !showPlaceQuickCreate ? (
                        <AsyncSearchableSelect
                          triggerRef={dialogPlaceTriggerRef}
                          openSignal={placeOpenSignal}
                          onTriggerKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            e.preventDefault();
                            if (!txPersonId || editingInventory.available < 1 || transactionMutation.isPending) return;
                            handleTransaction(editingThing.id, 'checkout');
                          }}
                          searchFn={searchPlaces}
                          selectedIds={txPlaceId ? [txPlaceId] : []}
                          initialOptions={txPlaceInitialOption && txPlaceId === txPlaceInitialOption.id ? [txPlaceInitialOption] : undefined}
                          onSelect={(opt) => { setTxPlaceId(opt.id); setTimeout(() => dialogPlaceTriggerRef.current?.focus(), 0); }}
                          placeholder="Where? (optional)"
                          searchPlaceholder="Search places..."
                          emptyMessage="No place found"
                          onCreateNew={(query) => {
                            setPlaceQuickCreateName(query);
                            setShowPlaceQuickCreate(true);
                          }}
                          createNewLabel="Create place"
                        />
                      ) : (
                        <div className="space-y-2 rounded-md border bg-background p-3">
                          <p className="text-xs font-medium">Quick-add place</p>
                          <input
                            type="text"
                            placeholder="Place name"
                            value={placeQuickCreateName}
                            onChange={(e) => setPlaceQuickCreateName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter') return;
                              e.preventDefault();
                              if (!placeQuickCreateName.trim() || quickCreatePlaceMutation.isPending) return;
                              quickCreatePlaceMutation.mutate({ name: placeQuickCreateName.trim() });
                            }}
                            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              disabled={!placeQuickCreateName.trim() || quickCreatePlaceMutation.isPending}
                              onClick={() => quickCreatePlaceMutation.mutate({ name: placeQuickCreateName.trim() })}
                            >
                              {quickCreatePlaceMutation.isPending ? 'Creating...' : 'Add'}
                            </Button>
                            <Button type="button" size="sm" variant="ghost" onClick={() => setShowPlaceQuickCreate(false)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )
                    )}

                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max={editingInventory.available}
                        value={txQuantity}
                        onChange={(e) => setTxQuantity(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                                onBlur={() => setTxQuantity((q) => (q === '' || q < 1) ? 1 : q)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          const mode = editingThing.tracking_mode === 'consumable' ? 'consume' : 'checkout';
                          if ((!txPersonId && mode !== 'consume') || editingInventory.available < 1 || transactionMutation.isPending) return;
                          handleTransaction(editingThing.id, mode);
                        }}
                        className="w-20 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={(!txPersonId && editingThing.tracking_mode !== 'consumable') || editingInventory.available < 1 || transactionMutation.isPending}
                        onClick={() => handleTransaction(editingThing.id, editingThing.tracking_mode === 'consumable' ? 'consume' : 'checkout')}
                      >
                        {transactionMutation.isPending ? '...' : editingThing.tracking_mode === 'consumable' ? (
                          <><Flame className="mr-1 h-3 w-3" /> Consume</>
                        ) : (
                          <><ArrowUpRight className="mr-1 h-3 w-3" /> Checkout</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  {editingInventory.recent_transactions.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent Activity</p>
                      <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                      {editingInventory.recent_transactions.map((tx: ThingInventoryState['recent_transactions'][number]) => (
                        <div key={tx.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                          {tx.type === 'checkout' && <ArrowUpRight className="h-3 w-3 text-blue-500" />}
                          {tx.type === 'return' && <ArrowDownLeft className="h-3 w-3 text-green-500" />}
                          {tx.type === 'consume' && <Flame className="h-3 w-3 text-orange-500" />}
                          <span className="font-medium text-foreground">{tx.person_name ?? 'Unknown'}</span>
                          <span>{tx.type} &times;{tx.quantity}</span>
                          {tx.place_name && (
                            <span className="inline-flex items-center gap-0.5">
                              <MapPin className="h-3 w-3" />
                              {tx.place_name}
                            </span>
                          )}
                          <span className="ml-auto whitespace-nowrap" title={new Date(tx.created_at).toLocaleString()}>
                            {new Date(tx.created_at).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                      ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <input
                    name="name"
                    defaultValue={editingThing.name}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    required
                    onChange={(e) => {
                      // Keep slug in sync if the user hasn't typed a custom slug
                      const slugField = (e.currentTarget.form?.elements.namedItem('slug') as HTMLInputElement | null);
                      if (slugField && slugField.value === editingThing.slug) {
                        slugField.value = slugify(e.target.value);
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Slug</label>
                  <input
                    name="slug"
                    defaultValue={editingThing.slug}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Lowercase, URL-safe identifier.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <input
                    name="type"
                    defaultValue={(editingThing as unknown as { category?: string }).category ?? editingThing.type}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    placeholder="equipment, instrument, furniture..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <select
                    name="status"
                    defaultValue={editingThing.status}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                  >
                    {THING_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tracking Mode</label>
                  <select
                    name="tracking_mode"
                    defaultValue={editingThing.tracking_mode ?? 'lendable'}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                  >
                    <option value="lendable">Lendable (check in/out)</option>
                    <option value="consumable">Consumable (used up)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Total Stock</label>
                  <input
                    name="quantity"
                    type="number"
                    min="1"
                    defaultValue={editingThing.quantity ?? 1}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Owner</label>
                {ownerQuickCreateContext === 'edit' ? (
                  <div className="space-y-2 rounded-md border bg-background p-3">
                    <p className="text-xs font-medium">Quick-add person</p>
                    <input
                      type="text"
                      placeholder="Name"
                      value={ownerQuickCreateName}
                      onChange={(e) => setOwnerQuickCreateName(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    />
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={ownerQuickCreatePhone}
                      onChange={(e) => setOwnerQuickCreatePhone(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={!ownerQuickCreateName.trim() || quickCreateOwnerMutation.isPending}
                        onClick={() => quickCreateOwnerMutation.mutate({ name: ownerQuickCreateName.trim(), phone: ownerQuickCreatePhone.trim() })}
                      >
                        {quickCreateOwnerMutation.isPending ? 'Creating...' : 'Add'}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setOwnerQuickCreateContext(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <AsyncSearchableSelect
                        searchFn={searchPeople}
                        selectedIds={editOwnerId ? [editOwnerId] : []}
                        initialOptions={ownerInitialOption && editOwnerId === ownerInitialOption.id ? [ownerInitialOption] : undefined}
                        onSelect={(opt) => {
                          setEditOwnerId(opt.id);
                          setOwnerInitialOption(opt);
                        }}
                        placeholder="No owner"
                        searchPlaceholder="Search people..."
                        emptyMessage="No one found"
                        onCreateNew={(query) => {
                          setOwnerQuickCreateName(query);
                          setOwnerQuickCreatePhone('');
                          setOwnerQuickCreateContext('edit');
                        }}
                        createNewLabel="Create person"
                      />
                    </div>
                    {editOwnerId && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setEditOwnerId(null)}>
                        Clear
                      </Button>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Optional. Person responsible for this item.</p>
              </div>
              <PhotoUploadField
                label="Image"
                helper="Upload a photo of this equipment"
                placeholder="https://"
                value={imageUrl}
                onChange={setImageUrl}
                category="thing_photo"
                identifier={editingThing.slug}
                accountSlug={account?.slug}
                allowBypassSizeLimit
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">Metadata (JSON)</label>
                <textarea
                  name="metadata"
                  value={metadataInput}
                  onChange={(e) => setMetadataInput(e.target.value)}
                  className="min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Store any structured data for this item. Leave blank for empty metadata.
                </p>
                {metadataError && <p className="text-xs text-destructive">{metadataError}</p>}
              </div>

              {canDelete && (
                <DangerZone
                  entityLabel="thing"
                  onDelete={() => handleDeleteClick({ id: editingThing.id, name: editingThing.name })}
                  disabled={deleteMutation.isPending}
                />
              )}
              </div>
              <DialogFooter className="gap-2 border-t pt-4 sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setEditingThing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deletingThing && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteConfirm}
          entityName={deletingThing.name}
          entityType="thing"
          dependencies={deleteDependencies || undefined}
          isDeleting={deleteMutation.isPending}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {selectedThingIds.size > 0 && (
        <DeleteConfirmationDialog
          open={bulkDeleteDialogOpen}
          onOpenChange={setBulkDeleteDialogOpen}
          onConfirm={handleBulkDeleteConfirm}
          entityName=""
          entityType="thing"
          entityCount={selectedThingIds.size}
          dependencies={(() => {
            // Aggregate dependencies from all selected things
            const allDeps: DependencyCheckResult = {
              hasDependencies: false,
              dependencies: [],
            };
            const depMap = new Map<string, number>();
            const depItems = new Map<string, Array<{ id: string; name: string }>>();

            bulkDeleteDependencies.forEach((result) => {
              if (result.hasDependencies) {
                allDeps.hasDependencies = true;
                result.dependencies.forEach((dep) => {
                  const key = dep.type;
                  depMap.set(key, (depMap.get(key) || 0) + dep.count);
                  if (!depItems.has(key)) {
                    depItems.set(key, []);
                  }
                  depItems.get(key)!.push(...dep.items);
                });
              }
            });

            depMap.forEach((count, type) => {
              allDeps.dependencies.push({
                type,
                count,
                items: depItems.get(type) || [],
              });
            });

            return allDeps;
          })()}
          isDeleting={bulkDeleteMutation.isPending}
        />
      )}
    </div>
  );
}
